/**
 * Auth Store
 *
 * Manages Firebase authentication state.
 * Ported from: UserAuthManager.kt
 */

import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import {
  auth,
  signInWithGoogle,
  signOutUser,
  getIdToken,
  onAuthStateChanged,
  type User,
} from '../config/firebase.js';

interface AuthUser {
  userId: string;
  email?: string;
  displayName?: string;
  photoURL?: string;
}

interface AuthState {
  user: AuthUser | null;
  sessionToken: string | null;
  isLoading: boolean;
  isInitialized: boolean;
  error: string | null;

  // YouTube cookies for authenticated streams (SAPISID, etc.)
  youtubeCookies: string | null;

  // Actions
  initAuth: () => void;
  signIn: () => Promise<void>;
  signOut: () => Promise<void>;
  refreshToken: () => Promise<string | null>;
  setYouTubeCookies: (cookies: string | null) => void;
  setError: (error: string | null) => void;
}

/**
 * Convert Firebase User to our AuthUser type
 */
function toAuthUser(user: User): AuthUser {
  return {
    userId: user.uid,
    email: user.email || undefined,
    displayName: user.displayName || undefined,
    photoURL: user.photoURL || undefined,
  };
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      sessionToken: null,
      isLoading: false,
      isInitialized: false,
      error: null,
      youtubeCookies: null,

      /**
       * Initialize auth state listener
       * Call this once on app start
       */
      initAuth: () => {
        set({ isLoading: true });

        onAuthStateChanged(async (firebaseUser) => {
          if (firebaseUser) {
            const token = await firebaseUser.getIdToken();
            set({
              user: toAuthUser(firebaseUser),
              sessionToken: token,
              isLoading: false,
              isInitialized: true,
              error: null,
            });
          } else {
            set({
              user: null,
              sessionToken: null,
              isLoading: false,
              isInitialized: true,
            });
          }
        });
      },

      /**
       * Sign in with Google
       */
      signIn: async () => {
        set({ isLoading: true, error: null });
        try {
          const firebaseUser = await signInWithGoogle();
          const token = await firebaseUser.getIdToken();
          set({
            user: toAuthUser(firebaseUser),
            sessionToken: token,
            isLoading: false,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign in failed';
          set({ error: message, isLoading: false });
          throw error;
        }
      },

      /**
       * Sign out
       */
      signOut: async () => {
        set({ isLoading: true });
        try {
          await signOutUser();
          set({
            user: null,
            sessionToken: null,
            youtubeCookies: null,
            isLoading: false,
          });
        } catch (error) {
          const message = error instanceof Error ? error.message : 'Sign out failed';
          set({ error: message, isLoading: false });
        }
      },

      /**
       * Refresh the ID token
       */
      refreshToken: async () => {
        const token = await getIdToken(true);
        if (token) {
          set({ sessionToken: token });
        }
        return token;
      },

      /**
       * Set YouTube cookies for authenticated streams
       * These are needed for SAPISID hash authentication
       */
      setYouTubeCookies: (cookies) => {
        set({ youtubeCookies: cookies });
      },

      setError: (error) => set({ error }),
    }),
    {
      name: 'zemer-auth',
      partialize: (state) => ({
        user: state.user,
        sessionToken: state.sessionToken,
        youtubeCookies: state.youtubeCookies,
      }),
    }
  )
);
